/**
 * Custom Hook for Payment Schedules Management
 * Manages state and operations for invoice payment schedules
 * Migrated to TanStack Query for automatic caching and refetching
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  paymentSchedulesAPI,
  PaymentSchedule,
  PaymentScheduleStats,
  GenerateSchedulesParams,
  CreateScheduleParams,
  UpdateScheduleParams,
} from '../utils/paymentSchedulesAPI';
import { useAuth } from './useAuth';

export interface UsePaymentSchedulesOptions {
  invoiceId?: string;
  autoLoad?: boolean;
}

export interface UsePaymentSchedulesReturn {
  // State
  schedules: PaymentSchedule[];
  stats: PaymentScheduleStats | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  // Actions
  loadSchedules: (invoiceId: string) => Promise<void>;
  loadStats: (invoiceId: string) => Promise<void>;
  loadUpcoming: (days?: number) => Promise<void>;
  createSchedule: (params: CreateScheduleParams) => Promise<PaymentSchedule>;
  generateSchedules: (invoiceId: string, params: GenerateSchedulesParams) => Promise<PaymentSchedule[]>;
  updateSchedule: (scheduleId: string, params: UpdateScheduleParams) => Promise<PaymentSchedule>;
  markAsPaid: (scheduleId: string, paidAt?: string) => Promise<PaymentSchedule>;
  deleteSchedule: (scheduleId: string) => Promise<void>;
  clearError: () => void;
  reload: () => Promise<void>;
}

// Query keys
const paymentScheduleKeys = {
  all: ['payment-schedules'] as const,
  byInvoice: (tenantId?: string, companyId?: string, invoiceId?: string) =>
    [...paymentScheduleKeys.all, 'by-invoice', tenantId, companyId, invoiceId] as const,
  stats: (tenantId?: string, companyId?: string, invoiceId?: string) =>
    [...paymentScheduleKeys.all, 'stats', tenantId, companyId, invoiceId] as const,
  upcoming: (tenantId?: string, companyId?: string, days?: number) =>
    [...paymentScheduleKeys.all, 'upcoming', tenantId, companyId, days] as const,
};

/**
 * Hook for managing payment schedules
 */
export function usePaymentSchedules(
  options: UsePaymentSchedulesOptions = {}
): UsePaymentSchedulesReturn {
  const { invoiceId: initialInvoiceId, autoLoad = false } = options;
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const companyId = user?.companyId;
  const queryClient = useQueryClient();

  // Track which invoice we're currently looking at
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string | undefined>(initialInvoiceId);
  const [manualError, setManualError] = useState<string | null>(null);

  /**
   * Query: payment schedules for an invoice
   */
  const {
    data: schedules = [],
    isLoading: schedulesLoading,
    error: schedulesError,
  } = useQuery<PaymentSchedule[], Error>({
    queryKey: paymentScheduleKeys.byInvoice(tenantId, companyId, currentInvoiceId),
    queryFn: () => paymentSchedulesAPI.getByInvoice(currentInvoiceId!),
    enabled: !!tenantId && !!currentInvoiceId && (autoLoad || !!currentInvoiceId),
    staleTime: 30 * 1000,
  });

  /**
   * Query: payment schedule stats for an invoice
   */
  const {
    data: stats = null,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery<PaymentScheduleStats | null, Error>({
    queryKey: paymentScheduleKeys.stats(tenantId, companyId, currentInvoiceId),
    queryFn: () => paymentSchedulesAPI.getStats(currentInvoiceId!),
    enabled: !!tenantId && !!currentInvoiceId && (autoLoad || !!currentInvoiceId),
    staleTime: 30 * 1000,
  });

  /**
   * Mutation: create schedule
   */
  const createMutation = useMutation({
    mutationFn: (params: CreateScheduleParams) => paymentSchedulesAPI.create(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la creation de l\'echeancier';
      setManualError(errorMessage);
    },
  });

  /**
   * Mutation: generate schedules
   */
  const generateMutation = useMutation({
    mutationFn: ({ invoiceId, params }: { invoiceId: string; params: GenerateSchedulesParams }) =>
      paymentSchedulesAPI.generate(invoiceId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la generation des echeanciers';
      setManualError(errorMessage);
    },
  });

  /**
   * Mutation: update schedule
   */
  const updateMutation = useMutation({
    mutationFn: ({ scheduleId, params }: { scheduleId: string; params: UpdateScheduleParams }) =>
      paymentSchedulesAPI.update(scheduleId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la mise a jour de l\'echeancier';
      setManualError(errorMessage);
    },
  });

  /**
   * Mutation: mark as paid
   */
  const markAsPaidMutation = useMutation({
    mutationFn: ({ scheduleId, paidAt }: { scheduleId: string; paidAt?: string }) =>
      paymentSchedulesAPI.markAsPaid(scheduleId, paidAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors du marquage comme paye';
      setManualError(errorMessage);
    },
  });

  /**
   * Mutation: delete schedule
   */
  const deleteMutation = useMutation({
    mutationFn: (scheduleId: string) => paymentSchedulesAPI.delete(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la suppression de l\'echeancier';
      setManualError(errorMessage);
    },
  });

  /**
   * Load payment schedules for an invoice
   */
  const loadSchedules = useCallback(async (invoiceId: string) => {
    setManualError(null);
    setCurrentInvoiceId(invoiceId);
    await queryClient.invalidateQueries({
      queryKey: paymentScheduleKeys.byInvoice(tenantId, companyId, invoiceId),
    });
  }, [tenantId, companyId, queryClient]);

  /**
   * Load statistics for an invoice's payment schedules
   */
  const loadStats = useCallback(async (invoiceId: string) => {
    setManualError(null);
    setCurrentInvoiceId(invoiceId);
    await queryClient.invalidateQueries({
      queryKey: paymentScheduleKeys.stats(tenantId, companyId, invoiceId),
    });
  }, [tenantId, companyId, queryClient]);

  /**
   * Load upcoming payment schedules (next N days)
   */
  const loadUpcoming = useCallback(async (days: number = 30) => {
    setManualError(null);
    setCurrentInvoiceId(undefined);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: paymentScheduleKeys.upcoming(tenantId, companyId, days),
        queryFn: () => paymentSchedulesAPI.getUpcoming(days),
      });
      // Set upcoming data into the schedules query cache for the current view
      queryClient.setQueryData(
        paymentScheduleKeys.byInvoice(tenantId, companyId, undefined),
        data
      );
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors du chargement des echeanciers a venir';
      setManualError(errorMessage);
    }
  }, [tenantId, companyId, queryClient]);

  /**
   * Create a new payment schedule
   */
  const createSchedule = useCallback(
    async (params: CreateScheduleParams): Promise<PaymentSchedule> => {
      setManualError(null);
      return createMutation.mutateAsync(params);
    },
    [createMutation]
  );

  /**
   * Auto-generate payment schedules for an invoice
   */
  const generateSchedules = useCallback(
    async (invoiceId: string, params: GenerateSchedulesParams): Promise<PaymentSchedule[]> => {
      setManualError(null);
      const result = await generateMutation.mutateAsync({ invoiceId, params });
      return result || [];
    },
    [generateMutation]
  );

  /**
   * Update a payment schedule
   */
  const updateSchedule = useCallback(
    async (scheduleId: string, params: UpdateScheduleParams): Promise<PaymentSchedule> => {
      setManualError(null);
      return updateMutation.mutateAsync({ scheduleId, params });
    },
    [updateMutation]
  );

  /**
   * Mark a payment schedule as paid
   */
  const markAsPaid = useCallback(
    async (scheduleId: string, paidAt?: string): Promise<PaymentSchedule> => {
      setManualError(null);
      return markAsPaidMutation.mutateAsync({ scheduleId, paidAt });
    },
    [markAsPaidMutation]
  );

  /**
   * Delete a payment schedule
   */
  const deleteSchedule = useCallback(
    async (scheduleId: string): Promise<void> => {
      setManualError(null);
      await deleteMutation.mutateAsync(scheduleId);
    },
    [deleteMutation]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setManualError(null);
  }, []);

  /**
   * Reload current data
   */
  const reload = useCallback(async () => {
    if (currentInvoiceId) {
      await queryClient.invalidateQueries({ queryKey: paymentScheduleKeys.all });
    }
  }, [currentInvoiceId, queryClient]);

  // Combine loading states
  const loading = schedulesLoading || statsLoading;
  const saving = createMutation.isPending || generateMutation.isPending ||
    updateMutation.isPending || markAsPaidMutation.isPending || deleteMutation.isPending;

  // Combine error states
  const error = manualError ||
    (schedulesError ? (schedulesError instanceof Error ? schedulesError.message : 'Erreur lors du chargement des echeanciers') : null) ||
    (statsError ? (statsError instanceof Error ? statsError.message : 'Erreur lors du chargement des statistiques') : null);

  return {
    // State
    schedules,
    stats,
    loading,
    saving,
    error,

    // Actions
    loadSchedules,
    loadStats,
    loadUpcoming,
    createSchedule,
    generateSchedules,
    updateSchedule,
    markAsPaid,
    deleteSchedule,
    clearError,
    reload,
  };
}
